import type { Media } from '@/payload-types'
import { normalizeHttpsUrl } from '@/lib/urls/normalizeHttpsUrl'
import { getMediaUrl } from '@/utilities/getMediaUrl'

export type ApartmentImage = {
  alt: string
  height?: number
  src: string
  width?: number
}

export type ApartmentGalleryImageItem = {
  alt: string
  height?: number
  id: string
  src: string
  width?: number
}

export type ApartmentGalleryComparisonItem = {
  after: ApartmentImage
  before: ApartmentImage
  id: string
}

export type ApartmentGalleryTourItem = {
  embedUrl: string
  id: string
  title: string
}

/** @deprecated Use ApartmentGalleryTourItem */
export type ApartmentGalleryVideoItem = ApartmentGalleryTourItem

export type ApartmentGalleryComparisonSlide = {
  item: ApartmentGalleryComparisonItem
  kind: 'comparison'
}

export type ApartmentGalleryImageSlide = {
  item: ApartmentGalleryImageItem
  kind: 'image'
}

export type ApartmentGallerySlide = ApartmentGalleryComparisonSlide | ApartmentGalleryImageSlide

export type ApartmentGalleryViewId = 'gallery' | 'floorPlans' | 'view360'

export const APARTMENT_GALLERY_VIEW_TAB_ORDER: ApartmentGalleryViewId[] = [
  'gallery',
  'view360',
  'floorPlans',
]

export type ApartmentGalleryViews = {
  /** Ordinary gallery images (shown after furnished comparisons in the detail carousel). */
  gallery: ApartmentGalleryImageItem[]
  /** Floor plan images for the dedicated Floor plans tab. */
  floorPlans: ApartmentGalleryImageItem[]
  view360: ApartmentGalleryTourItem | null
  /** Before/after comparisons (shown first in the detail gallery carousel). */
  furnished: ApartmentGalleryComparisonItem[]
}

type ImageRow = {
  id?: string | null
  image?: number | Media | null
}

type View360Group = {
  tourUrl?: string | null
  videoTitle?: string | null
}

type FurnishedViewRow = {
  afterImage?: number | Media | null
  beforeImage?: number | Media | null
  id?: string | null
}

/** @deprecated Use `normalizeHttpsUrl` directly. Kept for existing imports/tests. */
export const normalizeApartmentTourUrl = normalizeHttpsUrl

function resolveMedia(media: number | Media | null | undefined): Media | null {
  if (!media || typeof media === 'number') return null
  return media
}

function mapMediaToApartmentImage(
  media: Media | null,
  fallbackAlt: string,
): ApartmentImage | null {
  const src = getMediaUrl(media?.url)
  if (!src) return null

  return {
    alt: media?.alt?.trim() || fallbackAlt,
    src,
    ...(media?.height != null ? { height: media.height } : {}),
    ...(media?.width != null ? { width: media.width } : {}),
  }
}

export function mapApartmentGalleryImages(
  rows: ImageRow[] | null | undefined,
  fallbackAlt: string,
): ApartmentGalleryImageItem[] {
  const items: ApartmentGalleryImageItem[] = []

  for (const [index, row] of (rows ?? []).entries()) {
    const image = mapMediaToApartmentImage(resolveMedia(row.image), fallbackAlt)
    if (!image) continue

    items.push({
      id: row.id?.trim() || `gallery-image-${index}`,
      alt: image.alt,
      src: image.src,
      ...(image.height != null ? { height: image.height } : {}),
      ...(image.width != null ? { width: image.width } : {}),
    })
  }

  return items
}

export function mapApartmentFloorPlans(
  rows: ImageRow[] | null | undefined,
  fallbackAlt: string,
): ApartmentGalleryImageItem[] {
  const items: ApartmentGalleryImageItem[] = []

  for (const [index, row] of (rows ?? []).entries()) {
    const image = mapMediaToApartmentImage(resolveMedia(row.image), fallbackAlt)
    if (!image) continue

    items.push({
      id: row.id?.trim() || `floor-plan-${index}`,
      alt: image.alt,
      src: image.src,
      ...(image.height != null ? { height: image.height } : {}),
      ...(image.width != null ? { width: image.width } : {}),
    })
  }

  return items
}

export function mapApartmentView360(
  group: View360Group | null | undefined,
  fallbackAlt: string,
): ApartmentGalleryTourItem | null {
  if (!group) return null

  const embedUrl = normalizeApartmentTourUrl(group.tourUrl)
  if (!embedUrl) return null

  return {
    id: 'view360',
    embedUrl,
    title: group.videoTitle?.trim() || fallbackAlt,
  }
}

export function mapApartmentFurnishedView(
  rows: FurnishedViewRow[] | null | undefined,
  fallbackAlt: string,
): ApartmentGalleryComparisonItem[] {
  const items: ApartmentGalleryComparisonItem[] = []

  for (const [index, row] of (rows ?? []).entries()) {
    const before = mapMediaToApartmentImage(resolveMedia(row.beforeImage), fallbackAlt)
    const after = mapMediaToApartmentImage(resolveMedia(row.afterImage), fallbackAlt)
    if (!before || !after) continue

    items.push({
      id: row.id?.trim() || `furnished-${index}`,
      before,
      after,
    })
  }

  return items
}

export function mapApartmentGalleryViews(args: {
  fallbackAlt: string
  floorPlans?: ImageRow[] | null
  furnishedView?: FurnishedViewRow[] | null
  images?: ImageRow[] | null
  view360?: View360Group | null
}): ApartmentGalleryViews {
  return {
    gallery: mapApartmentGalleryImages(args.images, args.fallbackAlt),
    floorPlans: mapApartmentFloorPlans(args.floorPlans, args.fallbackAlt),
    view360: mapApartmentView360(args.view360, args.fallbackAlt),
    furnished: mapApartmentFurnishedView(args.furnishedView, args.fallbackAlt),
  }
}

/** Detail gallery slides: all furnished comparisons first, then ordinary images. */
export function buildApartmentGallerySlides(views: ApartmentGalleryViews): ApartmentGallerySlide[] {
  const slides: ApartmentGallerySlide[] = []

  for (const item of views.furnished) {
    slides.push({ kind: 'comparison', item })
  }

  for (const item of views.gallery) {
    slides.push({ kind: 'image', item })
  }

  return slides
}

export function buildApartmentFloorPlanSlides(
  floorPlans: ApartmentGalleryImageItem[],
): ApartmentGalleryImageSlide[] {
  return floorPlans.map((item) => ({ kind: 'image', item }))
}

export function getEnabledGalleryViews(views: ApartmentGalleryViews): ApartmentGalleryViewId[] {
  const tabs: ApartmentGalleryViewId[] = []

  if (buildApartmentGallerySlides(views).length > 0) {
    tabs.push('gallery')
  }

  if (views.view360) {
    tabs.push('view360')
  }

  if (views.floorPlans.length > 0) {
    tabs.push('floorPlans')
  }

  return tabs
}

/** @deprecated Use getEnabledGalleryViews */
export function getAvailableGalleryViews(views: ApartmentGalleryViews): ApartmentGalleryViewId[] {
  return getEnabledGalleryViews(views)
}

export function getApartmentHeroImage(views: ApartmentGalleryViews): ApartmentImage | null {
  const firstGalleryImage = views.gallery[0]
  if (!firstGalleryImage) return null

  return {
    alt: firstGalleryImage.alt,
    src: firstGalleryImage.src,
    ...(firstGalleryImage.height != null ? { height: firstGalleryImage.height } : {}),
    ...(firstGalleryImage.width != null ? { width: firstGalleryImage.width } : {}),
  }
}

export function getApartmentListingImages(
  gallery: ApartmentGalleryImageItem[] | ApartmentImage[],
): ApartmentImage[] {
  return gallery.map(({ alt, src, height, width }) => ({
    alt,
    src,
    ...(height != null ? { height } : {}),
    ...(width != null ? { width } : {}),
  }))
}

export function hasApartmentGalleryContent(views: ApartmentGalleryViews): boolean {
  return (
    buildApartmentGallerySlides(views).length > 0 ||
    views.floorPlans.length > 0 ||
    views.view360 != null
  )
}
